<template>
  <div>
    <div class="panel">
      <div class="viewer" :class="{ 'viewer--paused': paused }">
        <video
          ref="videoRef"
          class="video-feed"
          autoplay
          muted
          playsinline
        />
        <canvas ref="canvasRef" class="overlay-canvas" />
        <div class="scan-guide" aria-hidden="true" />
        <div v-if="paused" class="camera-paused-overlay">
          <button
            type="button"
            class="primary camera-resume-btn"
            :disabled="starting"
            @click="resumeCamera"
          >
            {{ starting ? 'Starte…' : 'Kamera aktivieren' }}
          </button>
          <div class="camera-paused-hint">
            Scanner pausiert – Akku sparen
          </div>
        </div>
      </div>
    </div>

    <div class="result">
      <div class="result-main">
        <div class="result-thumb-wrap">
          <img
            v-if="imageUrl"
            :src="imageUrl"
            class="result-thumb"
            alt=""
            loading="lazy"
            referrerpolicy="no-referrer"
            @error="onImageError"
          />
          <div v-else class="result-thumb result-thumb--placeholder" aria-hidden="true"></div>
        </div>
        <div class="result-main-text">
          <h2 class="result-barcode">{{ barcodeDisplay }}</h2>
          <h1 class="result-title">{{ productTitle }}</h1>
          <div
            v-if="regelnummer || fachnummer"
            class="result-attr-pills"
            role="group"
            aria-label="Regal- und Fachnummer"
          >
            <span v-if="regelnummer" class="pill pill--shelf">Regel: {{ regelnummer }}</span>
            <span v-if="fachnummer" class="pill pill--shelf">Fach: {{ fachnummer }}</span>
          </div>
          <div v-if="articleNumber" class="result-article-number">
            Art.-Nr.: {{ articleNumber }}
          </div>
        </div>
      </div>

      <div v-if="errorBox" class="meta error" style="margin-top: 10px">{{ errorBox }}</div>

      <div class="result-footer">
        <div v-if="lastScanAgoText" class="meta meta--footer">{{ lastScanAgoText }}</div>
        <div v-if="barcodeExtra" class="meta meta--footer">{{ barcodeExtra }}</div>
        <div class="meta meta--footer">{{ lookupSummary }}</div>
      </div>
    </div>

    <div class="status-bar status-bar--bottom" aria-label="Status, Kamera und API-Test">
      <div class="status-bar-pills">
        <div class="pill">{{ cameraPill }}</div>
        <div class="pill">{{ meshPill }}</div>
        <div class="pill">{{ scanPill }}</div>
        <div class="pill">{{ apiPill }}</div>
      </div>
      <div class="status-bar-footer-actions">
        <button
          v-if="!running"
          type="button"
          class="primary status-bar-camera-btn"
          :disabled="starting"
          @click="startAll"
        >
          {{ starting ? 'Starte…' : 'Kamera starten' }}
        </button>
        <button
          v-if="running"
          type="button"
          class="secondary status-bar-camera-btn"
          @click="stopAll"
        >
          Kamera stoppen
        </button>
        <button
          type="button"
          class="secondary status-bar-api-btn"
          :disabled="apiTestBusy"
          title="Test: GET /variants mit fester EAN"
          @click="runApiTest"
        >
          {{ apiTestBusy ? 'API Test…' : 'API Test' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import * as tf from '@tensorflow/tfjs-core';
import '@tensorflow/tfjs-converter';
import '@tensorflow/tfjs-backend-webgl';
import * as facemesh from '@tensorflow-models/facemesh';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { normalizeGtin } from '@/utils/barcode.js';
import { searchProductByGtin, API_TEST_EAN } from '@/services/shopwareApi.js';

/** Gleiche GTIN nicht innerhalb dieser Zeit erneut an die API senden */
const SAME_GTIN_COOLDOWN_MS = 8000;

const videoRef = ref(null);
const canvasRef = ref(null);

const starting = ref(false);
const running = ref(false);
/** True, wenn Kamera + Loop wegen erfolgreichem Treffer pausiert sind (Akku sparen) */
const paused = ref(false);

const cameraPill = ref('Kamera: aus');
const meshPill = ref('FaceMesh: lädt nicht');
const scanPill = ref('Barcode: aus');
const apiStatus = ref('bereit');

const barcodeDisplay = ref('Noch kein Barcode erkannt');
const barcodeExtra = ref('');
/** Zeitstempel des letzten erfolgreichen Scans (ms since epoch) — reaktiv für Anzeige */
const lastSeenAt = ref(0);
/** Tick-Ref, die jede Sekunde aktualisiert wird, um die „Letzter Scan vor …"-Anzeige live zu halten */
const nowTick = ref(Date.now());

const productTitle = ref('—');
const errorBox = ref('');

const lastScannedCode = ref('');
const lastLookupAt = ref(0);
/** Zuletzt an die API gesendete GTIN (für Suchsperre) */
const lastApiGtin = ref('');

const lastHitTitle = ref('');
const lastLookupWasFound = ref(null);

/** Artikelnummer (nach erfolgreicher Suche) */
const articleNumber = ref('');
/** Shopware attribute.attr1 / attr2 */
const regelnummer = ref('');
const fachnummer = ref('');
/** Thumbnail des Artikels (absolute URL) */
const imageUrl = ref('');
const apiTestBusy = ref(false);

let stream = null;
let faceModel = null;
let barcodeReader = null;
let barcodeControls = null;
let rafId = null;
let ctx = null;
/** Timer-Handle für das Sekunden-Tick (1 Hz) */
let secondTickIntervalId = null;

let lookupInFlight = false;

const apiPill = computed(() => {
  const m = {
    bereit: 'API: bereit',
    suche: 'API: suche…',
    gefunden: 'API: gefunden',
    nicht_gefunden: 'API: nicht gefunden',
    fehler: 'API: Fehler',
  };
  return m[apiStatus.value] || `API: ${apiStatus.value}`;
});

const lastScanAgoText = computed(() => {
  if (!lastSeenAt.value) return '';
  const seconds = Math.max(0, Math.floor((nowTick.value - lastSeenAt.value) / 1000));
  const label = seconds === 1 ? 'Sekunde' : 'Sekunden';
  return `Letzter Scan vor ${seconds} ${label}`;
});

const lookupSummary = computed(() => {
  const ts = lastLookupAt.value;
  if (!ts) return 'Noch keine Suche ausgeführt.';
  const ago = Math.max(0, Math.round((Date.now() - ts) / 1000));
  const base = `Zuletzt gesucht vor ${ago}s`;
  if (lastLookupWasFound.value === true && lastHitTitle.value) {
    return `Letzter Treffer: ${lastHitTitle.value} — ${base}`;
  }
  if (lastLookupWasFound.value === false) {
    return `${base} — keine Treffer`;
  }
  return base;
});

function setError(message) {
  errorBox.value = message || '';
}

function resetResultsForStart() {
  productTitle.value = '—';
  apiStatus.value = 'bereit';
  lastLookupWasFound.value = null;
  lastHitTitle.value = '';
  articleNumber.value = '';
  regelnummer.value = '';
  fachnummer.value = '';
  imageUrl.value = '';
  setError('');
}

function applyArticleAttrs(res) {
  if (!res || !res.found) {
    regelnummer.value = '';
    fachnummer.value = '';
    articleNumber.value = '';
    imageUrl.value = '';
    return;
  }
  regelnummer.value = res.regelnummer || '';
  fachnummer.value = res.fachnummer || '';
  articleNumber.value = res.articleNumber || '';
  imageUrl.value = res.imageUrl || '';
}

function onImageError() {
  imageUrl.value = '';
}

async function runApiTest() {
  if (apiTestBusy.value) return;
  apiTestBusy.value = true;
  apiStatus.value = 'suche';
  setError('');
  try {
    const res = await searchProductByGtin(API_TEST_EAN);
    lastLookupAt.value = Date.now();
    lastApiGtin.value = API_TEST_EAN;
    applyArticleAttrs(res);

    if (res.found) {
      apiStatus.value = 'gefunden';
      productTitle.value = res.title || '—';
      lastHitTitle.value = res.title || '';
      lastLookupWasFound.value = true;
      barcodeDisplay.value = `${API_TEST_EAN} (API Test)`;
      barcodeExtra.value = 'Manueller Test: GET /variants';
      lastScannedCode.value = API_TEST_EAN;
      lastSeenAt.value = Date.now();
      nowTick.value = Date.now();
      // Erfolgreichen Scan simulieren: Kamera + Loop pausieren (Akku sparen).
      pauseForResult();
    } else {
      apiStatus.value = 'nicht_gefunden';
      productTitle.value = 'Kein Artikel gefunden';
      lastHitTitle.value = '';
      lastLookupWasFound.value = false;
      barcodeDisplay.value = `${API_TEST_EAN} (API Test)`;
      barcodeExtra.value = 'Kein Treffer für Test-EAN';
      applyArticleAttrs(null);
    }
  } catch (e) {
    apiStatus.value = 'fehler';
    productTitle.value = '—';
    lastLookupWasFound.value = null;
    applyArticleAttrs(null);
    const detail = e.body ? ` ${JSON.stringify(e.body).slice(0, 200)}` : '';
    setError(`API-Fehler: ${e.message || e}${detail}`);
  } finally {
    apiTestBusy.value = false;
  }
}

async function ensureFaceMesh() {
  if (faceModel) return faceModel;
  meshPill.value = 'FaceMesh: lädt';
  try {
    await tf.setBackend('webgl');
    await tf.ready();
    faceModel = await facemesh.load({ maxFaces: 1 });
    meshPill.value = 'FaceMesh: aktiv';
    return faceModel;
  } catch {
    faceModel = null;
    meshPill.value = 'FaceMesh: aus';
    return null;
  }
}

async function startCamera() {
  const constraints = {
    audio: false,
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
  };

  stream = await navigator.mediaDevices.getUserMedia(constraints);
  const video = videoRef.value;
  video.srcObject = stream;
  await new Promise((resolve) => {
    video.onloadedmetadata = () => resolve();
  });
  await video.play();

  const canvas = canvasRef.value;
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  ctx = canvas.getContext('2d', { willReadFrequently: true });
  cameraPill.value = 'Kamera: aktiv';
}

function drawMesh(predictions) {
  if (!predictions || !predictions.length || !ctx) return;
  ctx.fillStyle = '#4ade80';
  for (const pred of predictions) {
    const points = pred.scaledMesh || [];
    for (let i = 0; i < points.length; i += 4) {
      const p = points[i];
      ctx.beginPath();
      ctx.arc(p[0], p[1], 1.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

async function renderLoop() {
  if (!running.value) return;
  const video = videoRef.value;
  const canvas = canvasRef.value;
  if (!video || !canvas || !ctx) {
    rafId = requestAnimationFrame(renderLoop);
    return;
  }

  if (video.readyState < 2) {
    rafId = requestAnimationFrame(renderLoop);
    return;
  }

  if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  if (faceModel) {
    try {
      const predictions = await faceModel.estimateFaces(video, false, false);
      drawMesh(predictions);
    } catch {
      /* Frame-Fehler ignorieren — kein Overlay, keine Nutzermeldung */
    }
  }

  rafId = requestAnimationFrame(renderLoop);
}

async function maybeLookupProduct(rawText) {
  const gtin = normalizeGtin(rawText);
  if (!gtin) return;

  const now = Date.now();
  if (
    gtin === lastApiGtin.value &&
    lastLookupAt.value &&
    now - lastLookupAt.value < SAME_GTIN_COOLDOWN_MS
  ) {
    barcodeExtra.value = 'Suchsperre: gleiche GTIN (Cooldown aktiv)';
    return;
  }

  if (lookupInFlight) return;

  lookupInFlight = true;
  apiStatus.value = 'suche';
  setError('');

  try {
    const res = await searchProductByGtin(gtin);
    lastLookupAt.value = Date.now();
    lastApiGtin.value = gtin;

    if (res.found) {
      apiStatus.value = 'gefunden';
      productTitle.value = res.title || '—';
      lastHitTitle.value = res.title || '';
      lastLookupWasFound.value = true;
      applyArticleAttrs(res);
      // Treffer vorhanden → Kamera + Loop pausieren (spart Akku).
      // User reaktiviert die Kamera manuell über den Overlay-Button.
      pauseForResult();
    } else {
      apiStatus.value = 'nicht_gefunden';
      productTitle.value = 'Kein Artikel gefunden';
      lastHitTitle.value = '';
      lastLookupWasFound.value = false;
      applyArticleAttrs(null);
    }
  } catch (e) {
    apiStatus.value = 'fehler';
    productTitle.value = '—';
    lastLookupWasFound.value = null;
    applyArticleAttrs(null);
    const detail = e.body ? ` ${JSON.stringify(e.body).slice(0, 200)}` : '';
    setError(`API-Fehler: ${e.message || e}${detail}`);
  } finally {
    lookupInFlight = false;
  }
}

function onBarcodeResult(text) {
  if (!text) return;
  lastScannedCode.value = text;
  lastSeenAt.value = Date.now();
  nowTick.value = Date.now();
  barcodeDisplay.value = text;
  barcodeExtra.value = '';
  scanPill.value = 'Barcode: erkannt';

  void maybeLookupProduct(text);
}

async function startBarcodeReader() {
  const video = videoRef.value;
  const reader = new BrowserMultiFormatReader();
  barcodeReader = reader;
  scanPill.value = 'Barcode: sucht';

  await reader.decodeFromVideoElement(video, (result, error, controls) => {
    if (controls && !barcodeControls) barcodeControls = controls;

    if (result) {
      const text =
        typeof result.getText === 'function' ? result.getText() : result.text || '';
      if (text) onBarcodeResult(text);
    }

    if (error) {
      const msg = String(error.message != null ? error.message : error);
      if (error.name === 'NotFoundException') return;
      if (msg.includes('No MultiFormat Readers were able to detect')) return;
    }
  });
}

/**
 * Kamera + ZXing + RAF-Loop sauber abbauen. Wird sowohl von stopAll (User-Stop)
 * als auch von pauseForResult (Auto-Pause nach Treffer) genutzt.
 * Belässt Ergebnis-Refs und apiStatus unangetastet.
 */
function teardownCamera() {
  running.value = false;
  if (rafId) cancelAnimationFrame(rafId);
  rafId = null;

  if (barcodeControls && typeof barcodeControls.stop === 'function') {
    try {
      barcodeControls.stop();
    } catch (_) {
      /* ignore */
    }
  }
  barcodeControls = null;

  if (barcodeReader && typeof barcodeReader.reset === 'function') {
    try {
      barcodeReader.reset();
    } catch (_) {
      /* ignore */
    }
  }
  barcodeReader = null;

  if (stream) {
    stream.getTracks().forEach((track) => track.stop());
    stream = null;
  }

  const video = videoRef.value;
  const canvas = canvasRef.value;
  if (video) video.srcObject = null;
  if (canvas && ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }
}

/**
 * Gemeinsamer Start-Pfad für startAll() und resumeCamera(). Initialisiert
 * FaceMesh (falls nötig), öffnet die Kamera, startet ZXing und die Render-Loop.
 */
async function bootCameraPipeline() {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    throw new Error('getUserMedia wird von diesem Browser nicht unterstützt');
  }
  await ensureFaceMesh();
  await startCamera();
  running.value = true;
  await startBarcodeReader();
  renderLoop();
}

async function startAll() {
  if (running.value || starting.value) return;
  starting.value = true;
  paused.value = false;
  resetResultsForStart();
  barcodeDisplay.value = 'Noch kein Barcode erkannt';
  barcodeExtra.value = '';
  lastScannedCode.value = '';
  lastSeenAt.value = 0;
  lastLookupAt.value = 0;
  lastApiGtin.value = '';

  try {
    await bootCameraPipeline();
  } catch (err) {
    running.value = false;
    cameraPill.value = 'Kamera: Fehler';
    scanPill.value = 'Barcode: Fehler';
    meshPill.value = faceModel ? 'FaceMesh: bereit' : 'FaceMesh: aus';
    setError(err.message || String(err));
  } finally {
    starting.value = false;
  }
}

/**
 * Kamera nach Auto-Pause wieder aufnehmen, ohne das aktuelle Ergebnis
 * zu verwerfen. Der Cooldown für die zuletzt gesuchte GTIN wird zurückgesetzt,
 * damit derselbe Artikel direkt wieder auslösen kann.
 */
async function resumeCamera() {
  if (running.value || starting.value) return;
  starting.value = true;
  setError('');
  try {
    await bootCameraPipeline();
    paused.value = false;
    lastApiGtin.value = '';
    scanPill.value = 'Barcode: sucht';
  } catch (err) {
    running.value = false;
    cameraPill.value = 'Kamera: Fehler';
    scanPill.value = 'Barcode: Fehler';
    setError(err.message || String(err));
  } finally {
    starting.value = false;
  }
}

/**
 * Auto-Pause nach erfolgreichem API-Treffer: spart Akku, weil Kamera-Track
 * und Render-/FaceMesh-Loop wirklich stoppen. User reaktiviert manuell.
 */
function pauseForResult() {
  if (!running.value) return;
  teardownCamera();
  paused.value = true;
  cameraPill.value = 'Kamera: pausiert';
  scanPill.value = 'Barcode: pausiert';
}

function stopAll() {
  teardownCamera();
  paused.value = false;
  cameraPill.value = 'Kamera: aus';
  scanPill.value = 'Barcode: aus';
  meshPill.value = faceModel ? 'FaceMesh: bereit' : 'FaceMesh: lädt nicht';
  apiStatus.value = 'bereit';
}

function onPageHide() {
  stopAll();
}

onMounted(() => {
  window.addEventListener('pagehide', onPageHide);
  secondTickIntervalId = window.setInterval(() => {
    nowTick.value = Date.now();
  }, 1000);
  void nextTick(() => {
    void startAll();
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('pagehide', onPageHide);
  if (secondTickIntervalId != null) {
    window.clearInterval(secondTickIntervalId);
    secondTickIntervalId = null;
  }
  stopAll();
});
</script>
